/*
  地の文に書いた JSX の部品や型引数のタグ (<Profile />、Array<string>、<T>、<p className="x">) を、字のまま出す。

  本文は生の HTML を通すので、markdown の解析はこれらを HTML のタグとして読む。描く側は知らない要素を
  中身だけ残して外す (rehypeSanitizeLessonHtml) ため、「代わりに <Profile /> を置く」は「代わりに  を置く」になっていた。
  ここでは、タグ 1 つだけの HTML のノードのうち、次のどれかに当たるものを素の字に戻す。
  - 名前が大文字で始まり小文字も含む (React の部品 <Button />、TypeScript の型 <HTMLInputElement>)。
    全部大文字の <BR> や <SVG> は HTML の要素として描く (HTML のタグ名は大文字小文字を区別しない)
  - 描く要素にも消す要素にも無い名前 (<string>、<name>、<T>)。HTML として描けず、そのまま消えるだけ
  <string, number> のように名前の直後に空白以外が来る形は、markdown の解析がもともとタグと読まないので字のまま出る。
  - 属性が JSX の書き方 (className=、htmlFor=、={…})。HTML には無い書き方
  書き手が置いた HTML (<mark>、<br>、svg の部品、details など) は今まで通り要素として描く。
  本文の markdown は書き換えず、描くときの木だけを直す。
*/
import { isKnownLessonHtmlElement } from "./rehypeSanitizeLessonHtml";

type Node = { type: string; value?: string; children?: Node[] };

const SINGLE_TAG = /^<\/?([A-Za-z][\w.:-]*)(\s[^<>]*?)?\s*\/?>$/;
const JSX_ATTRIBUTE = /\s(?:className|htmlFor)\s*=|=\s*\{/;

export function isProseTag(html: string): boolean {
  const match = SINGLE_TAG.exec(html.trim());
  if (!match) return false;
  const [, name, attributes = ""] = match;
  return (/^[A-Z]/.test(name) && /[a-z]/.test(name)) || !isKnownLessonHtmlElement(name) || JSX_ATTRIBUTE.test(attributes);
}

// 段落の中に置ける (字の並びの) ノード。ここに入っている HTML のノードは、タグ 1 つずつに分かれている
const PHRASING_PARENTS = new Set(["paragraph", "heading", "tableCell", "emphasis", "strong", "delete", "link", "linkReference"]);
const TAG = /<\/?[A-Za-z][^<>]*>/g;

/**
 * 行の頭の <Header /> や <Section> は、HTML の塊 (行ごと生の HTML) として読まれる (header、section は塊を始める名前)。
 * 頭のタグが地の文のタグなら、塊を段落に戻し、地の文のタグは字に、ほかのタグは今まで通り HTML にする
 */
function proseBlockToParagraph(html: string): Node | null {
  const first = html.trimStart().match(/^<\/?[A-Za-z][^<>]*>/);
  if (!first || !isProseTag(first[0])) return null;
  const children: Node[] = [];
  let last = 0;
  for (const match of html.matchAll(TAG)) {
    const at = match.index ?? 0;
    if (at > last) children.push({ type: "text", value: html.slice(last, at) });
    children.push({ type: isProseTag(match[0]) ? "text" : "html", value: match[0] });
    last = at + match[0].length;
  }
  if (last < html.length) children.push({ type: "text", value: html.slice(last) });
  return { type: "paragraph", children };
}

function walk(node: Node): void {
  if (!node.children) return;
  const phrasing = PHRASING_PARENTS.has(node.type);
  node.children = node.children.map((child) => {
    if (child.type !== "html" || typeof child.value !== "string") {
      walk(child);
      return child;
    }
    if (phrasing) return isProseTag(child.value) ? { ...child, type: "text" } : child;
    return proseBlockToParagraph(child.value) ?? child;
  });
}

export function remarkProseTagsAsText() {
  return (tree: Node): void => walk(tree);
}
